import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Button, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import AppDrawer from "../common/AppDrawer";
import ConfirmDialog from "../common/confirm-dialog";
import { BRAND_ORANGE, BRAND_ORANGE_HOVER } from "../../constants/constants";
import { useApiWithHandling } from "../../hooks/useApiWithHandling";
import { useSnackbar } from "../../contexts/snackbar-context";
import { Folder } from "./document-list";

interface MoveFolderPickerDrawerProps {
  open: boolean;
  onClose: () => void;
  folders: Folder[];
  currentFolderPath: string[];
  selectedCount: number;
  /** Folder IDs currently being moved — invalid as destinations (self + descendants). */
  movingFolderIds?: Set<string>;
  airframeId?: string;
  tailNumber?: string;
  onFolderCreated?: () => void | Promise<void>;
  onConfirm: (
    targetFolderId: string,
    targetFolderName: string,
    targetFolderPath: string
  ) => void;
}

const normalizeFolderId = (id: string): string => id.trim().toLowerCase();

const findFolderById = (
  folderList: Folder[],
  id: string
): Folder | null => {
  const normalizedId = normalizeFolderId(id);
  for (const folder of folderList) {
    if (normalizeFolderId(folder.id) === normalizedId) {
      return folder;
    }
    const found = findFolderById(folder.children, id);
    if (found) {
      return found;
    }
  }
  return null;
};

const parseCreateFolderResponse = (
  response: { data?: { data?: CreateCustomFolderApiData | string } } | null
): { id: string | null; path: string } => {
  const payload = response?.data?.data;
  if (!payload) {
    return { id: null, path: "" };
  }

  if (typeof payload === "string") {
    return { id: payload, path: "" };
  }

  return {
    id: payload.id ?? payload.Id ?? null,
    path: payload.path ?? payload.Path ?? "",
  };
};

const buildCreatedFolder = (
  id: string,
  name: string,
  path: string,
  parentFolder: Folder | null
): Folder => ({
  id,
  name,
  code: "CUSTOM",
  path,
  description: "",
  parentId: parentFolder?.id ?? null,
  children: [],
  documents: [],
  totalDocumentCount: 0,
  level: (parentFolder?.level ?? 0) + 1,
  hasChildren: false,
  hasDocuments: false,
});

const injectFolderIntoTree = (
  folderList: Folder[],
  parentId: string,
  newFolder: Folder
): Folder[] =>
  folderList.map((folder) => {
    if (normalizeFolderId(folder.id) === normalizeFolderId(parentId)) {
      const hasDuplicate = folder.children.some(
        (child) => normalizeFolderId(child.id) === normalizeFolderId(newFolder.id)
      );
      if (hasDuplicate) {
        return folder;
      }

      return {
        ...folder,
        children: [...folder.children, newFolder],
        hasChildren: true,
      };
    }

    if (folder.children.length === 0) {
      return folder;
    }

    return {
      ...folder,
      children: injectFolderIntoTree(folder.children, parentId, newFolder),
    };
  });

const isSelectableFolder = (folder: Folder): boolean => {
  if (!folder.id) return false;
  const guidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidPattern.test(folder.id);
};

const collectAncestorFolderIds = (
  folderList: Folder[],
  targetId: string,
  ancestors: string[] = []
): string[] | null => {
  for (const folder of folderList) {
    if (folder.id === targetId) {
      return ancestors;
    }

    const found = collectAncestorFolderIds(folder.children, targetId, [
      ...ancestors,
      folder.id,
    ]);
    if (found) {
      return found;
    }
  }

  return null;
};

const expandFolderPath = (
  folderId: string,
  folderList: Folder[],
  setExpandedIds: React.Dispatch<React.SetStateAction<Set<string>>>
) => {
  const ancestorIds = collectAncestorFolderIds(folderList, folderId);
  if (!ancestorIds) {
    return;
  }

  setExpandedIds((previous) => {
    const next = new Set(previous);
    for (const id of ancestorIds) {
      next.add(id);
    }
    next.add(folderId);
    return next;
  });
};

interface CreateCustomFolderApiData {
  id?: string;
  Id?: string;
  path?: string;
  Path?: string;
}

const isCurrentFolder = (folder: Folder, ancestorNames: string[], currentFolderPath: string[]): boolean => {
  const folderPath = [...ancestorNames, folder.name];
  if (folderPath.length !== currentFolderPath.length) {
    return false;
  }

  return folderPath.every(
    (name, index) => name.localeCompare(currentFolderPath[index], undefined, { sensitivity: "accent" }) === 0
  );
};

const FolderNameLabel: React.FC<{
  name: string;
  fontWeight: number;
  indentLevel: number;
}> = ({ name, fontWeight, indentLevel }) => {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) {
      return;
    }

    const updateTruncation = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    };

    updateTruncation();
    const observer = new ResizeObserver(updateTruncation);
    observer.observe(element);
    return () => observer.disconnect();
  }, [name, indentLevel]);

  return (
    <Tooltip title={isTruncated ? name : ""} placement="right" arrow>
      <Typography
        ref={textRef}
        sx={{
          fontSize: "0.9rem",
          fontWeight,
          color: "inherit",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Typography>
    </Tooltip>
  );
};

interface FolderTreeRowProps {
  folder: Folder;
  ancestorNames: string[];
  currentFolderPath: string[];
  expandedIds: Set<string>;
  selectedFolderId: string | null;
  isCreatingFolder: boolean;
  newFolderName: string;
  newFolderError: string;
  isSubmittingFolder: boolean;
  isInvalidDestination: (folder: Folder) => boolean;
  onToggleExpand: (folderId: string) => void;
  onSelect: (folder: Folder) => void;
  onNewFolderNameChange: (value: string) => void;
  onCancelCreateFolder: () => void;
  onCreateFolder: () => void;
}

const FolderTreeRow: React.FC<FolderTreeRowProps> = ({
  folder,
  ancestorNames,
  currentFolderPath,
  expandedIds,
  selectedFolderId,
  isCreatingFolder,
  newFolderName,
  newFolderError,
  isSubmittingFolder,
  isInvalidDestination,
  onToggleExpand,
  onSelect,
  onNewFolderNameChange,
  onCancelCreateFolder,
  onCreateFolder,
}) => {
  const hasChildren = (folder.children?.length ?? 0) > 0;
  const expanded = expandedIds.has(folder.id);
  const current = isCurrentFolder(folder, ancestorNames, currentFolderPath);
  const invalidDestination = isInvalidDestination(folder);
  const blocked = current || invalidDestination;
  const selected =
    !blocked && selectedFolderId === folder.id;
  const indentLevel = ancestorNames.length;
  const folderLabel = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
        flex: 1,
        opacity: blocked ? 0.4 : 1,
        cursor: blocked ? "default" : "pointer",
      }}
    >
      <FolderIcon sx={{ color: BRAND_ORANGE, fontSize: 20 }} />
      <FolderNameLabel
        name={folder.name}
        fontWeight={selected ? 700 : 500}
        indentLevel={indentLevel}
      />
    </Box>
  );

  const blockedTooltipTitle = current
    ? "Cannot move to current folder — select a subfolder instead"
    : invalidDestination
      ? "Cannot move a folder into itself or its subfolders"
      : "";

  return (
    <Box>
      <Box
        onClick={() => {
          onToggleExpand(folder.id);
          if (!current && !isInvalidDestination(folder)) {
            onSelect(folder);
          }
        }}
        sx={{
          minHeight: 44,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.75,
          borderRadius: 0.5,
          opacity: 1,
          cursor: blocked && !hasChildren ? "default" : "pointer",
          color: "#fff",
          backgroundColor:
            selectedFolderId === folder.id && !blocked
              ? "rgba(230, 81, 0, 0.25)"
              : "transparent",
          "&:hover":
            blocked && !hasChildren
              ? undefined
              : {
                  backgroundColor: selected
                    ? "rgba(230, 81, 0, 0.3)"
                    : BRAND_ORANGE_HOVER,
                },
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpand(folder.id);
            }}
            sx={{ color: "hsl(19, 101%, 51%)", opacity: 1, p: 0.25 }}
            aria-label={expanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
          >
            {expanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 28 }} />
        )}
        {blocked ? (
          <Tooltip
            title={blockedTooltipTitle}
            placement="right"
            arrow
          >
            {folderLabel}
          </Tooltip>
        ) : (
          folderLabel
        )}
      </Box>
      {selectedFolderId === folder.id &&
        isCreatingFolder && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: `${(indentLevel + 1) * 16 + 8}px`,
            pr: 1,
            py: 0.5,
          }}
        >
          <FolderIcon
            sx={{
              fontSize: 18,
              color: "hsl(19, 101%, 51%)",
              opacity: 0.6,
            }}
          />
          <TextField
            autoFocus
            size="small"
            placeholder="Folder name"
            value={newFolderName}
            onChange={(e) => onNewFolderNameChange(e.target.value)}
            error={!!newFolderError}
            helperText={newFolderError}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCreateFolder();
              if (e.key === "Escape") onCancelCreateFolder();
            }}
            sx={{
              flex: 1,
              "& .MuiInputBase-input": {
                color: "#fff",
                fontSize: "0.85rem",
                padding: "4px 8px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "hsl(19, 101%, 51%)",
              },
              "& .MuiFormHelperText-root": {
                color: "#f44336",
                fontSize: "0.7rem",
                margin: "2px 0 0",
              },
            }}
            InputProps={{ sx: { backgroundColor: "rgba(255,255,255,0.05)" } }}
          />
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              void onCreateFolder();
            }}
            disabled={!newFolderName.trim() || isSubmittingFolder}
            sx={{ color: "hsl(19, 101%, 51%)", padding: "4px" }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onCancelCreateFolder();
            }}
            sx={{ color: "rgba(255,255,255,0.5)", padding: "4px" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {hasChildren && expanded && (
        <Box sx={{ pl: 2.5 }}>
          {folder.children.map((child) => (
            <FolderTreeRow
              key={child.id}
              folder={child}
              ancestorNames={[...ancestorNames, folder.name]}
              currentFolderPath={currentFolderPath}
              expandedIds={expandedIds}
              selectedFolderId={selectedFolderId}
              isCreatingFolder={isCreatingFolder}
              newFolderName={newFolderName}
              newFolderError={newFolderError}
              isSubmittingFolder={isSubmittingFolder}
              isInvalidDestination={isInvalidDestination}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
              onNewFolderNameChange={onNewFolderNameChange}
              onCancelCreateFolder={onCancelCreateFolder}
              onCreateFolder={onCreateFolder}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const MoveFolderPickerDrawer: React.FC<MoveFolderPickerDrawerProps> = ({
  open,
  onClose,
  folders,
  currentFolderPath,
  selectedCount,
  movingFolderIds,
  airframeId,
  tailNumber,
  onFolderCreated,
  onConfirm,
}) => {
  const { post } = useApiWithHandling();
  const { showSuccess } = useSnackbar();
  const [localFolders, setLocalFolders] = useState(folders);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] =
    useState(false);
  const [newFolderName, setNewFolderName] =
    useState("");
  const [newFolderError, setNewFolderError] =
    useState("");
  const [isSubmittingFolder, setIsSubmittingFolder] =
    useState(false);
  const [pendingSelectFolderId, setPendingSelectFolderId] =
    useState<string | null>(null);
  const [pendingSelectFolderName, setPendingSelectFolderName] =
    useState<string | null>(null);
  const [pendingParentFolderId, setPendingParentFolderId] =
    useState<string | null>(null);

  const selectedFolderId = selectedFolder?.id ?? null;

  const selectCreatedFolder = useCallback(
    (newFolder: Folder, parentFolder: Folder | null) => {
      setSelectedFolder(newFolder);
      if (parentFolder) {
        expandFolderPath(parentFolder.id, localFolders, setExpandedIds);
      }
      expandFolderPath(newFolder.id, localFolders, setExpandedIds);
    },
    [localFolders]
  );

  const isInvalidDestination = useCallback(
    (folder: Folder): boolean => {
      // Can't move into itself
      if (movingFolderIds?.has(folder.id)) {
        return true;
      }

      // Can't move into a descendant of any folder being moved
      if (movingFolderIds && movingFolderIds.size > 0) {
        for (const movingId of Array.from(movingFolderIds)) {
          const movingFolder = findFolderById(localFolders, movingId);
          if (
            movingFolder &&
            folder.path.startsWith(movingFolder.path + "/")
          ) {
            return true;
          }
        }
      }

      return false;
    },
    [localFolders, movingFolderIds]
  );

  useEffect(() => {
    if (
      pendingSelectFolderId &&
      pendingParentFolderId &&
      selectedFolder &&
      !findFolderById(folders, pendingSelectFolderId)
    ) {
      setLocalFolders(
        injectFolderIntoTree(folders, pendingParentFolderId, selectedFolder)
      );
      return;
    }

    setLocalFolders(folders);
  }, [folders, pendingSelectFolderId, pendingParentFolderId, selectedFolder]);

  useEffect(() => {
    if (!open) {
      setExpandedIds(new Set());
      setSelectedFolder(null);
      setIsConfirmOpen(false);
      setIsCreatingFolder(false);
      setNewFolderName("");
      setNewFolderError("");
      setIsSubmittingFolder(false);
      setPendingSelectFolderId(null);
      setPendingSelectFolderName(null);
      setPendingParentFolderId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!pendingSelectFolderId && !pendingSelectFolderName) {
      return;
    }

    let newFolder: Folder | null = null;
    if (pendingSelectFolderId) {
      newFolder = findFolderById(localFolders, pendingSelectFolderId);
    }

    const parentId = pendingParentFolderId ?? selectedFolderId;
    if (!newFolder && pendingSelectFolderName && parentId) {
      const parentFolder = findFolderById(localFolders, parentId);
      newFolder =
        parentFolder?.children?.find(
          (child) =>
            child.name.localeCompare(pendingSelectFolderName, undefined, {
              sensitivity: "accent",
            }) === 0
        ) ?? null;
    }

    if (!newFolder) {
      return;
    }

    selectCreatedFolder(newFolder, parentId ? findFolderById(localFolders, parentId) : null);
    setPendingSelectFolderId(null);
    setPendingSelectFolderName(null);
    setPendingParentFolderId(null);
  }, [
    localFolders,
    pendingSelectFolderId,
    pendingSelectFolderName,
    pendingParentFolderId,
    selectedFolderId,
    selectCreatedFolder,
  ]);

  useEffect(() => {
    if (selectedFolder && isInvalidDestination(selectedFolder)) {
      setSelectedFolder(null);
    }
  }, [selectedFolder, isInvalidDestination]);

  const handleToggleExpand = (folderId: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const buildFullFolderPath = (
    folderId: string,
    allFolders: Folder[]
  ): string => {
    const findPath = (
      folders: Folder[],
      targetId: string,
      currentPath: string[]
    ): string[] | null => {
      for (const folder of folders) {
        const newPath = [...currentPath, folder.name];
        if (folder.id === targetId) return newPath;
        const found = findPath(folder.children, targetId, newPath);
        if (found) return found;
      }
      return null;
    };
    return findPath(allFolders, folderId, [])?.join("/") ?? "";
  };

  const handleConfirmMove = () => {
    if (!selectedFolder || isInvalidDestination(selectedFolder)) {
      return;
    }

    // Standard IATA nodes use code-only paths in `path`; blob storage needs display names.
    const fullPath = buildFullFolderPath(selectedFolder.id, localFolders);
    onConfirm(selectedFolder.id, selectedFolder.name, fullPath || selectedFolder.path);
    setIsConfirmOpen(false);
    onClose();
  };

  const handleCancelCreateFolder = () => {
    setIsCreatingFolder(false);
    setNewFolderName("");
    setNewFolderError("");
  };

  const handleNewFolderNameChange = (value: string) => {
    setNewFolderName(value);
    setNewFolderError("");
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name || !selectedFolderId) return;

    if (!tailNumber?.trim()) {
      setNewFolderError("Tail number is required to create a folder.");
      return;
    }

    const parentFolder = findFolderById(
      localFolders,
      selectedFolderId
    );
    if (!parentFolder) {
      setNewFolderError("Selected folder not found. Please select a folder again.");
      return;
    }

    const duplicate = parentFolder.children?.some(
      (child) => child.name.toLowerCase() === name.toLowerCase()
    );

    if (duplicate) {
      setNewFolderError(
        "A folder with this name already exists here"
      );
      return;
    }

    const parentFolderId = parentFolder.id;
    setIsSubmittingFolder(true);
    try {
      const isCustomParent =
        parentFolder.code === "CUSTOM" && isSelectableFolder(parentFolder);
      const response = await post<{ data?: CreateCustomFolderApiData | string }>(
        "api/documents/custom-folder",
        {
          name,
          tailNumber,
          parentCustomFolderId: isCustomParent ? selectedFolderId : null,
          attachedIataNodeKey:
            !isCustomParent && parentFolder.code !== "CUSTOM"
              ? parentFolder.path
              : null,
        },
        {
          showLoader: true,
          showErrorSnackbar: true,
          showSuccessSnackbar: false,
          throwOnError: true,
        }
      );

      const { id: createdFolderId, path: createdFolderPath } =
        parseCreateFolderResponse(response);

      if (!createdFolderId) {
        setNewFolderError("Folder was created but could not be selected. Please select it manually.");
        return;
      }

      const createdFolder = buildCreatedFolder(
        createdFolderId,
        name,
        createdFolderPath,
        parentFolder
      );

      setLocalFolders((previous) => {
        const nextTree = injectFolderIntoTree(previous, parentFolderId, createdFolder);
        setExpandedIds((expandedPrevious) => {
          const next = new Set(expandedPrevious);
          const ancestorIds = collectAncestorFolderIds(nextTree, parentFolderId);
          if (ancestorIds) {
            for (const id of ancestorIds) {
              next.add(id);
            }
          }
          next.add(parentFolderId);
          next.add(createdFolderId);
          return next;
        });
        return nextTree;
      });
      setSelectedFolder(createdFolder);
      setPendingSelectFolderId(createdFolderId);
      setPendingSelectFolderName(name);
      setPendingParentFolderId(parentFolderId);

      setIsCreatingFolder(false);
      setNewFolderName("");
      setNewFolderError("");

      if (onFolderCreated) {
        await onFolderCreated();
      }

      showSuccess(`Folder "${name}" created — select Move files when ready.`);
    } catch {
      setNewFolderError(
        "Failed to create folder. Please try again."
      );
    } finally {
      setIsSubmittingFolder(false);
    }
  };

  return (
    <>
      <AppDrawer
        open={open}
        onClose={onClose}
        title={`Move ${(() => {
          const fileCount = selectedCount;
          const folderCount = movingFolderIds?.size ?? 0;
          const fileLabel =
            fileCount === 1 ? "1 file" : `${fileCount} files`;
          const folderLabel =
            folderCount === 1 ? "1 folder" : `${folderCount} folders`;

          if (fileCount > 0 && folderCount > 0) {
            return `${fileLabel} and ${folderLabel}`;
          }
          if (folderCount > 0) {
            return folderLabel;
          }
          if (fileCount > 0) {
            return fileLabel;
          }
          return "items";
        })()} to...`}
        width={480}
        footerActions={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Tooltip
              title="Select a folder first"
              placement="top"
              disableHoverListener={!!selectedFolderId}
            >
              <span>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<CreateNewFolderIcon />}
                  disabled={!selectedFolderId || isCreatingFolder}
                  onClick={() => {
                    setIsCreatingFolder(true);
                    setNewFolderName("");
                    setNewFolderError("");
                  }}
                  sx={{
                    color:
                      selectedFolderId && !isCreatingFolder
                        ? "hsl(19, 101%, 51%)"
                        : "rgba(255,255,255,0.3)",
                    textTransform: "none",
                    fontSize: "0.85rem",
                  }}
                >
                  New folder
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="contained"
              disabled={!selectedFolderId || isSubmittingFolder}
              onClick={() => setIsConfirmOpen(true)}
              sx={{
                backgroundColor: "hsl(19, 101%, 51%)",
                color: "#fff",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "hsl(19, 90%, 45%)",
                },
              }}
            >
              Move files
            </Button>
          </Box>
        }
      >
        {localFolders.length === 0 ? (
          <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>
            No folders available.
          </Typography>
        ) : (
          localFolders.map((folder) => (
            <FolderTreeRow
              key={folder.id}
              folder={folder}
              ancestorNames={[]}
              currentFolderPath={currentFolderPath}
              expandedIds={expandedIds}
              selectedFolderId={selectedFolderId}
              isCreatingFolder={isCreatingFolder}
              newFolderName={newFolderName}
              newFolderError={newFolderError}
              isSubmittingFolder={isSubmittingFolder}
              isInvalidDestination={isInvalidDestination}
              onToggleExpand={handleToggleExpand}
              onSelect={setSelectedFolder}
              onNewFolderNameChange={handleNewFolderNameChange}
              onCancelCreateFolder={handleCancelCreateFolder}
              onCreateFolder={handleCreateFolder}
            />
          ))
        )}
      </AppDrawer>

      <ConfirmDialog
        open={isConfirmOpen}
        title="Move files"
        message={(() => {
          const fileCount = selectedCount;
          const folderCount = movingFolderIds?.size ?? 0;

          const parts: string[] = [];
          if (fileCount > 0) parts.push(`${fileCount} file(s)`);
          if (folderCount > 0) parts.push(`${folderCount} folder(s)`);

          const itemDesc = parts.join(" and ") || "items";
          return `You are about to move ${itemDesc} to '${selectedFolder?.name ?? ""}'. This action cannot be undone.`;
        })()}
        confirmLabel="Confirm move"
        cancelLabel="Cancel"
        onConfirm={handleConfirmMove}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </>
  );
};

export default MoveFolderPickerDrawer;
